import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingCart, type CartItem } from './ShoppingCart';
import { ImageModal } from './ImageModal';
import { Footer } from '@app/components/shared/Footer';
import { GiftListHeader } from './PublicGiftList/GiftListHeader';
import { RecipientsNavigation } from './PublicGiftList/RecipientsNavigation';
import { GiftGrid } from './PublicGiftList/GiftGrid';
import { BottomCTA } from './PublicGiftList/BottomCTA';
import ShoppingCartPage from './PublicGiftList/ShoppingCartPage';
import { trpc } from '@/utils/trpc';
import type { Gift } from '@shared/trpc/schemas';
import { cartIcon } from '@/assets';

export function PublicGiftList() {
  const { token } = useParams<{ token: string }>();
  const [showCart, setShowCart] = useState(false);
  const [showCartPage, setShowCartPage] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<number | null>(null);
  const [modalImage, setModalImage] = useState<{ url: string; alt: string } | null>(null);

  // Use tRPC query
  const {
    data,
    isLoading: loading,
    error,
  } = trpc.public.getGiftList.useQuery(
    { token: token! },
    {
      enabled: !!token,
    }
  );

  // Set first recipient as selected by default when data loads
  useEffect(() => {
    if (data && data.recipients && data.recipients.length > 0 && selectedRecipient === null) {
      setSelectedRecipient(data.recipients[0].id);
    }
  }, [data, selectedRecipient]);

  // Check if there are any processed gifts (Let Birdie Handle It was clicked)
  const getProcessedGifts = () => {
    if (!data?.recipients) return [];
    return data.recipients.flatMap(recipient => 
      recipient.gifts.filter(gift => gift.let_birdie_handle_it)
    );
  };

  const processedGifts = getProcessedGifts();
  
  // Extract hasSubscription flag from data
  const hasSubscription = data?.hasSubscription ?? false;

  const addToCart = (gift: Gift) => {
    // Prevent adding processed or purchased gifts to cart
    if (gift.let_birdie_handle_it || gift.buy_it_myself) {
      return;
    }
    
    setCartItems((prev) => {
      const existingItem = prev.find((item) => item.id === gift.id);
      if (existingItem) {
        return prev.map((item) => (item.id === gift.id ? { ...item, quantity: item.quantity + 1 } : item));
      }
      // Convert tRPC Gift to CartItem, handling nullable fields
      const cartItem: CartItem = {
        id: gift.id,
        title: gift.title,
        description: gift.description,
        url: gift.url,
        price: gift.price,
        image_url: gift.image_url || '', // Convert null to empty string (required in CartItem)
        image_data: gift.image_data || undefined, // Convert null to undefined (optional in CartItem)
        recipient_name: gift.recipient_name,
        quantity: 1
      } as CartItem;
      return [...prev, cartItem];
    });
    // Only auto-open cart on desktop (md and above)
    if (window.innerWidth >= 768) {
      setShowCart(true);
    }
  };

  const removeFromCart = (giftId: number) => {
    setCartItems((prev) => prev.filter((item) => item.id !== giftId));
  };

  const updateQuantity = (giftId: number, quantity: number) => {
    if (quantity === 0) {
      removeFromCart(giftId);
      return;
    }
    setCartItems((prev) => prev.map((item) => (item.id === giftId ? { ...item, quantity } : item)));
  };

  const scrollToRecipient = (recipientId: number) => {
    setSelectedRecipient(recipientId);
    const element = document.getElementById(`recipient-${recipientId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCartNext = () => {
    setShowCartPage(true);
    setShowCart(false);
  };

  const handleCartPageBack = () => {
    setShowCartPage(false);
    setShowCart(true);
  };

  const handleCartPageSubmit = (orderData: any) => {
    console.log('Order submitted:', orderData);
    // Here you would typically send the order to your backend
    // For now, we'll just log it
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-text-cream flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-footer border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-footer">Loading your gift recommendations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-text-cream flex items-center justify-center">
        <div className="max-w-md w-full mx-auto bg-white rounded-3xl shadow-soft p-8 text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-footer mb-2">Oops!</h1>
          <p className="text-soft-slate mb-4">{error.message || 'Failed to load gift list'}</p>
          <p className="text-sm text-soft-slate">
            The link may have expired or be invalid. Please contact support if you continue to have issues.
          </p>
        </div>
      </div>
    );
  }

  if (!data || !data.recipients) {
    return null;
  }

  // Show cart page if showCartPage is true
  if (showCartPage) {
    return (
      <ShoppingCartPage
        cartItems={cartItems}
        contactInfo={{
          name: data.event.contact_name,
          phone: data.event.contact_phone,
          email: data.event.contact_email,
        }}
        token={token!}
        onBack={handleCartPageBack}
        onSubmit={handleCartPageSubmit}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <GiftListHeader event={data.event} />
      <div className="relative">
        {/* Cart Sidebar */}
        <ShoppingCart
          key="shopping-cart"
          isVisible={showCart}
          onClose={() => setShowCart(false)}
          cartItems={cartItems}
          onUpdateQuantity={updateQuantity}
          onRemoveItem={removeFromCart}
          onNext={handleCartNext}
        />

        {/* Recipients Navigation */}
        <div className={`${showCart ? 'hidden md:block' : ''}`}>
          <RecipientsNavigation
            recipients={data.recipients}
            selectedRecipient={selectedRecipient}
            onRecipientSelect={scrollToRecipient}
          />
        </div>

        <div className={`max-w-[1240px] mx-auto py-8 px-4 ${showCart ? 'hidden md:block' : ''} ${showCart ? 'md:mr-[440px] lg:mr-[440px]' : ''} transition-all duration-300`}>
          <div className="grid grid-cols-1">
            {/* Main Content */}
            <div className="col-span-1">
              {/* Recipients and Gifts */}
              <GiftGrid
                recipients={data.recipients}
                showCart={showCart}
                token={token!}
                onAddToCart={addToCart}
                onImageClick={(imageUrl, title) => setModalImage({ url: imageUrl, alt: title })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <BottomCTA hasSubscription={hasSubscription} />

      {/* Footer */}
      <Footer />

      {/* Floating Cart Icon for Mobile */}
      {cartItems.length > 0 && !showCart && (
        <div className="fixed bottom-4 right-4 z-30 md:hidden">
          <button
            onClick={() => setShowCart(true)}
            className="bg-purple-medium text-white rounded-full p-4 shadow-lg flex items-center space-x-2"
            style={{ boxShadow: '0px 4px 20px 0px rgba(21, 93, 226, 0.18)' }}
          >
            <img src={cartIcon} alt="cart" className="w-6 h-6" />
            <span className="bg-white text-purple-medium rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
              {cartItems.reduce((total, item) => total + item.quantity, 0)}
            </span>
          </button>
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={!!modalImage}
        onClose={() => setModalImage(null)}
        imageUrl={modalImage?.url || ''}
        imageAlt={modalImage?.alt || ''}
      />
    </div>
  );
}
